import { useRef, useState } from 'react';
import clsx from 'clsx';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import usePostCandidateBoardsMove from '../api/usePostCandidateBoardsMove';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import type { CandidateProfileResponse } from '../types/CandidateProfile';
import type { AgentApiCandidateProfilesBoard } from '@/types/serializers';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

// ボードの color_type を Figma のタグ色トークン（bg-tag-* / text-tag-*）へ対応させる。
// pink は magenta トークン、yellow_green は該当トークンが無いため green にフォールバック。
const tagColorClass = (colorType?: string) => {
  switch (colorType) {
    case 'red':
      return 'bg-tag-red text-tag-red';
    case 'purple':
      return 'bg-tag-pueple text-tag-purple';
    case 'blue':
      return 'bg-tag-blue text-tag-blue';
    case 'yellow':
      return 'bg-tag-yellow text-tag-yellow';
    case 'orange':
      return 'bg-tag-orange text-tag-orange';
    case 'green':
    case 'yellow_green':
      return 'bg-tag-green text-tag-green';
    case 'pink':
      return 'bg-tag-magenta text-tag-magenta';
    case 'gray':
    default:
      return 'bg-tag-gray text-tag-gray';
  }
};

type Props = {
  candidatesBoards: AgentApiCandidateProfilesBoard[];
  candidateProfileId: number;
  candidateBoard?: {
    id?: number;
    title?: string;
    colorType?:
      'gray' | 'red' | 'purple' | 'blue' | 'yellow' | 'green' | 'pink' | 'yellow_green' | 'orange';
  };
  candidateProfileRefetch?: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<CandidateProfileResponse, Error>>;
  onBoardChanged?: () => void;
};

export default function CandidatesBoardsMenu({
  candidatesBoards,
  candidateProfileId,
  candidateBoard = undefined,
  candidateProfileRefetch,
  onBoardChanged,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => setIsOpen(false));

  const { mutateAsync: moveCandidate } = usePostCandidateBoardsMove();

  const onUpdateBoard = async (board: AgentApiCandidateProfilesBoard) => {
    try {
      await moveCandidate({
        candidate_profile_id: candidateProfileId,
        target_board_id: board.id,
        order_number: board.order_number,
      });
      await candidateProfileRefetch?.();
      setIsOpen(false);
      onBoardChanged?.();
    } catch (error) {
      showErrorToast('ボード移動に失敗しました');
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className={clsx(
          'flex cursor-pointer items-center justify-center rounded-full bg-white',
          isOpen && 'rounded-full'
        )}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <div
          className={clsx(
            'flex h-6 items-center rounded-full pr-xs pl-sm',
            tagColorClass(candidateBoard?.colorType)
          )}
        >
          <p className="text-tag-sm text-nowrap">
            {candidateBoard?.title ? candidateBoard?.title : '未分類'}
          </p>
          {isOpen ? (
            <MdKeyboardArrowUp className="size-5 opacity-50" />
          ) : (
            <MdKeyboardArrowDown className="size-5 opacity-50" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-6 right-0 z-50 w-[152px] overflow-y-auto rounded bg-white shadow-[0px_0px_12px_0px_#00000033]">
          {candidatesBoards.map((board) => (
            <div
              key={board.id}
              className={clsx(
                'flex h-xxl cursor-pointer items-center px-3 hover:bg-gray',
                board.id === candidateBoard?.id && 'bg-gray'
              )}
              onClick={() => onUpdateBoard(board)}
            >
              <span
                className={clsx(
                  'inline-flex h-6 items-center rounded-full px-sm text-tag-sm',
                  tagColorClass(board.color_type)
                )}
              >
                {board.title}
              </span>
              {board.id === candidateBoard?.id && (
                <img
                  src={`/agent/candidates/icon-check-circle.svg`}
                  width={16}
                  height={16}
                  alt=""
                  className="ml-auto"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
